import { create } from "zustand";
import { ResponseError } from "../types/Api";
import { useSessionStore } from "./SessionStore";

export interface AcademyDeviceConfig {
  classwize_enabled: boolean;
}

export interface AcademyDeviceConfigStore {
  config?: AcademyDeviceConfig;
  loading: boolean;
  error?: string;
  fetch: () => Promise<AcademyDeviceConfig>;
  save: (config: AcademyDeviceConfig) => Promise<AcademyDeviceConfig>;
  reset: () => void;
}

// Helper function to get the academy API base URL based on region
const getAcademyApiBaseUrl = (region: string): string => {
  // Check if we're in local development
  const isLocalDevelopment =
    typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  // For local development, use relative URLs that will go through the proxy
  if (isLocalDevelopment) {
    return "/academy";
  }

  // use region-specific URLs
  switch (region) {
    case "syd-2":
      return process.env.REACT_APP_ACADEMY_API_AU || "";
    case "us-1":
      return process.env.REACT_APP_ACADEMY_API_US || "";
    case "uk-1":
      return process.env.REACT_APP_ACADEMY_API_UK || "";
    case "sit":
      return process.env.REACT_APP_ACADEMY_API_SIT || "";
    case "beta-1":
      return process.env.REACT_APP_ACADEMY_API_STG || "";
    default:
      // Default to SIT for other regions or when region is not available
      return process.env.REACT_APP_ACADEMY_API_US || "";
  }
};

export const useAcademyDeviceConfigStore = create<AcademyDeviceConfigStore>((set) => ({
  config: undefined,
  loading: false,
  error: undefined,

  // GET request to retrieve current device configuration from the API
  fetch: async (): Promise<AcademyDeviceConfig> => {
    set({ loading: true, error: undefined });

    try {
      const { getDevice, getToken } = useSessionStore.getState();
      const device = getDevice();
      const token = getToken();
      const baseUrl = getAcademyApiBaseUrl(device.region);
      const url = `${baseUrl}/api/v1/device/${device.id}/config`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Device: device.id,
          Token: token,
        },
      });

      if (!response.ok) {
        throw new ResponseError(`Request failed with status ${response.status}`, response.status);
      }

      const data = (await response.json()) as { config: AcademyDeviceConfig };
      const config = data.config;

      set({ config, loading: false });
      return config;
    } catch (error) {
      const status = (error as ResponseError)?.status;
      let errorMessage = "Failed to fetch device config";

      if (status === 404) {
        errorMessage = "Device config API endpoint not found. Please ensure the academy-api is available.";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ error: errorMessage, loading: false });
      throw new ResponseError(errorMessage, status);
    }
  },

  // PUT request to update device configuration on the API
  save: async (config: AcademyDeviceConfig): Promise<AcademyDeviceConfig> => {
    set({ loading: true, error: undefined });

    try {
      const { getDevice, getToken } = useSessionStore.getState();
      const device = getDevice();
      const token = getToken();
      const baseUrl = getAcademyApiBaseUrl(device.region);
      const url = `${baseUrl}/api/v1/device/${device.id}/config`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Device: device.id,
          Token: token,
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new ResponseError(`Request failed with status ${response.status}`, response.status);
      }

      set({ config, loading: false });
      return config;
    } catch (error) {
      const status = (error as ResponseError)?.status;
      let errorMessage = "Failed to save device config";

      if (status === 404) {
        errorMessage = "Device config API endpoint not found. Please ensure the academy-api is available.";
      } else if (status === 400) {
        errorMessage = "Bad request: Invalid request format";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ error: errorMessage, loading: false });
      throw new ResponseError(errorMessage, status);
    }
  },

  reset: () => {
    set({ config: undefined, loading: false, error: undefined });
  },
}));
